/**
 * SurebetList - Main component for displaying list of surebet opportunities
 * Based on Listed_surebets.html mockup
 *
 * Renders filteredSurebets (client-side view filters from SurebetContext).
 * Raw surebets are used only for loading / API-empty / error checks.
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSurebet } from '../context/SurebetContext';
import { useTranslation } from '../../i18n/hooks';
import SurebetItem from './SurebetItem';
import SurebetListSkeleton from './SurebetListSkeleton';
import SurebetListEmpty from './SurebetListEmpty';
import SurebetListError from './SurebetListError';
import './SurebetList.css';

/**
 * SurebetList - Main component for displaying list of surebet opportunities
 *
 * Features:
 * - Responsive grid/list layout for surebet cards
 * - Loading skeleton during data fetching
 * - Empty state when no surebets available
 * - Filtered-empty state when view filters hide all results
 * - Error state handling with retry options
 * - Accessibility features for screen readers
 */
const SurebetList = ({
  className = '',
  showFilters = true,
  showRefresh = true,
  showStats = true,
  onSurebetClick,
  onCalculatorClick
}) => {
  const { t } = useTranslation();
  const {
    surebets,
    filteredSurebets,
    filteredCount,
    hasActiveViewFilters,
    resetViewFilters,
    loading,
    error,
    refreshSurebets,
    isRefreshing,
    totalSurebets
  } = useSurebet();

  const handleRefresh = async () => {
    await refreshSurebets();
  };

  const handleClearViewFilters = useCallback(() => {
    resetViewFilters();
  }, [resetViewFilters]);

  if (loading && surebets.length === 0) {
    return <SurebetListSkeleton className={className} />;
  }

  if (error && surebets.length === 0) {
    return (
      <SurebetListError
        error={error}
        onRetry={handleRefresh}
        className={className}
      />
    );
  }

  if (!loading && surebets.length === 0) {
    return <SurebetListEmpty className={className} />;
  }

  const showFilteredEmpty = filteredCount === 0 && hasActiveViewFilters;
  const listItems = Array.isArray(filteredSurebets) ? filteredSurebets : [];

  return (
    <div
      className={`surebet-list ${className}`}
      role="region"
      aria-label={t('surebets.list.ariaLabel')}
    >
      {showStats && (
        <div className="surebet-list__stats-header">
          <div className="surebet-list__stats">
            <h1 className="surebet-list__title">
              {t('surebets.list.title')}
            </h1>
            <div className="surebet-list__stats-details">
              <span className="surebet-list__stat surebet-list__stat--total">
                {t('surebets.list.total')}: <strong>{totalSurebets}</strong>
              </span>
            </div>
          </div>
        </div>
      )}

      {showRefresh && (
        <div className="surebet-list__header">
          <div className="surebet-list__stats">
            <h1 className="surebet-list__title">
              {t('surebets.list.title')}
            </h1>
            <div className="surebet-list__stats-details">
              <span className="surebet-list__stat surebet-list__stat--total">
                {t('surebets.list.total')}: <strong>{totalSurebets}</strong>
              </span>
            </div>
          </div>

          <div className="surebet-list__actions">
            <button
              className="surebet-list__refresh-btn"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label={t('surebets.list.refreshButton')}
            >
              <span
                className={`surebet-list__refresh-icon${isRefreshing ? ' surebet-list__refresh-icon--spinning' : ''}`}
                aria-hidden="true"
              >
                ⟳
              </span>
              <span>{t('surebets.list.refreshButton')}</span>
            </button>
          </div>
        </div>
      )}

      {showFilteredEmpty ? (
        <div className="surebet-list__filtered-empty" role="status">
          <h3>{t('surebets.viewFilters.emptyFilteredTitle')}</h3>
          <p>{t('surebets.viewFilters.emptyFilteredDescription')}</p>
          <button
            type="button"
            className="surebet-list__clear-filters-btn"
            onClick={handleClearViewFilters}
            aria-label={t('surebets.viewFilters.clearAllAriaLabel')}
          >
            {t('surebets.viewFilters.clearFilters')}
          </button>
        </div>
      ) : (
        <div className="surebet-list__container">
          {listItems.map((surebet) => (
            <SurebetItem
              key={surebet.id}
              surebet={surebet}
              onClick={onSurebetClick}
              onCalculatorClick={onCalculatorClick}
            />
          ))}
        </div>
      )}

      {isRefreshing && surebets.length > 0 && (
        <div
          className="surebet-list__refresh-overlay"
          role="status"
          aria-live="polite"
          aria-label={t('surebets.list.refreshing')}
        >
          <div className="surebet-list__refresh-spinner">
            <svg
              className="surebet-list__spinner-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="surebet-list__spinner-track"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="surebet-list__spinner-fill"
                d="M12 2a10 10 0 0 1 10 10"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
            <span>{t('surebets.list.refreshing')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

SurebetList.propTypes = {
  className: PropTypes.string,
  showFilters: PropTypes.bool,
  showRefresh: PropTypes.bool,
  showStats: PropTypes.bool,
  onSurebetClick: PropTypes.func,
  onCalculatorClick: PropTypes.func
};

export default SurebetList;
